import { ROLES, ROLE_HIERARCHY } from '@/constants';

/**
 * Directory role names: prefer JWT `roles` on the session account, else API `accountRoles`.
 * @param {object} [account]
 * @returns {string[]}
 */
function getDirectoryRoleNames(account) {
    if (Array.isArray(account?.tokenRoles) && account.tokenRoles.length) {
        return account.tokenRoles;
    }
    return account?.accountRoles?.map((ar) => ar?.role?.name).filter(Boolean) ?? [];
}

const BACKEND_NAME_TO_APP_ROLE = {
    Admin: ROLES.ADMIN,
    Executive: ROLES.EXECUTIVE,
    'Task Force Chair': ROLES.TASK_FORCE_CHAIR,
    'Pillar Chair': ROLES.PILLAR_CHAIR,
    'Project Manager': ROLES.PROJECT_MANAGER,
    Member: ROLES.MEMBER,
};

/**
 * Map COP-32 API role.name (seed / Prisma) to frontend route role constants.
 */
export function mapBackendRoleNameToAppRole(backendName) {
    if (!backendName) return ROLES.MEMBER;
    return BACKEND_NAME_TO_APP_ROLE[backendName] ?? ROLES.MEMBER;
}

/**
 * Choose the highest-privilege app role when an account has multiple roles.
 */
export function getPrimaryAppRoleFromAccount(account) {
    const names = getDirectoryRoleNames(account);
    if (!names.length) return ROLES.MEMBER;
    const appRoles = names.map((n) => mapBackendRoleNameToAppRole(n));
    return appRoles.reduce((best, r) =>
        ROLE_HIERARCHY.indexOf(r) > ROLE_HIERARCHY.indexOf(best) ? r : best
    );
}

/** Backend `role.name` for the highest-privilege role (for selects / labels). */
export function getPrimaryBackendRoleNameFromAccount(account) {
    const names = getDirectoryRoleNames(account);
    if (!names.length) return 'Member';
    return names.reduce((bestName, n) => {
        const r = mapBackendRoleNameToAppRole(n);
        const best = mapBackendRoleNameToAppRole(bestName);
        return ROLE_HIERARCHY.indexOf(r) > ROLE_HIERARCHY.indexOf(best) ? n : bestName;
    });
}

/** `role.id` for the highest-privilege backend role on the account. */
export function getPrimaryRoleIdFromAccount(account) {
    const primaryName = getPrimaryBackendRoleNameFromAccount(account);
    const ar = account?.accountRoles?.find((x) => x.role?.name === primaryName);
    return ar?.role?.id ?? account?.accountRoles?.[0]?.role?.id ?? '';
}

export function displayNameFromAccount(account) {
    const u = account?.user;
    if (!u) return account?.userName ?? '';
    const parts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
    return parts.length ? parts.join(' ') : account.userName;
}
